import { useCallback, useEffect, useState } from 'react'
import { Coins, Loader2, Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../../lib/api'
import { confirmDialog, toast } from '../../lib/dialogs'
import { formatCents } from '../../lib/money'
import {
  RateKindShort,
  type ClientRateDto,
  type ClientRateListResponse,
  type RateDto,
  type RateListResponse,
} from '@toga/shared'

interface Props {
  clientId: string
  canWrite: boolean
}

/**
 * Panel de tarifas asignadas a un cliente. Permite añadir una tarifa del
 * catálogo, opcionalmente con un importe override específico para ese cliente.
 * Se usa dentro del modal de edición de cliente.
 */
export default function ClientRatesPanel({ clientId, canWrite }: Props) {
  const { t } = useTranslation(['rates', 'common'])
  const [items, setItems] = useState<ClientRateDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { items } = await api.get<ClientRateListResponse>(`/clients/${clientId}/rates`)
      setItems(items)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [clientId, t])

  useEffect(() => {
    void reload()
  }, [reload])

  const onRemove = async (a: ClientRateDto) => {
    if (
      !(await confirmDialog({ message: t('confirmRemove', { name: a.rate.name }), tone: 'danger' }))
    )
      return
    try {
      await api.delete(`/clients/${clientId}/rates/${a.id}`)
      await reload()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('errors.generic'))
    }
  }

  return (
    <section className="rounded-lg border border-base p-3 space-y-3">
      <header className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider t-faint flex items-center gap-2">
          <Coins size={12} className="text-toga-700 dark:text-toga-300" /> {t('assignedRates')}
        </h3>
        {canWrite && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="text-[11px] px-2 py-1 rounded border border-base t-secondary hover:bg-hover hover:text-toga-700 dark:hover:text-toga-200 flex items-center gap-1"
          >
            <Plus size={11} /> {t('common:actions.add')}
          </button>
        )}
      </header>

      {loading && (
        <div className="text-xs t-muted flex items-center gap-2">
          <Loader2 size={12} className="animate-spin" /> {t('loading')}
        </div>
      )}

      {!loading && items.length === 0 && (
        <p className="text-xs t-faint">
          {t('emptyState')}
        </p>
      )}

      {!loading && items.length > 0 && (
        <ul className="divide-y divide-base/40 text-sm">
          {items.map((a) => {
            const applied = a.overrideAmountCents ?? a.rate.amountCents
            const hasOverride = a.overrideAmountCents != null
            return (
              <li key={a.id} className="py-2 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="t-primary truncate">{a.rate.name}</div>
                  <div className="text-[11px] t-faint flex items-center gap-2 flex-wrap">
                    <span>{t('enums:RateKind.' + a.rate.kind)}</span>
                    <span>·</span>
                    <span className="font-mono">
                      {formatCents(applied)} {RateKindShort[a.rate.kind]}
                    </span>
                    {hasOverride && (
                      <span className="text-amber-700 dark:text-amber-300">
                        ({t('baseLabel')} {formatCents(a.rate.amountCents)})
                      </span>
                    )}
                    {a.notes && <span className="t-muted">· {a.notes}</span>}
                  </div>
                </div>
                {canWrite && (
                  <button
                    type="button"
                    onClick={() => void onRemove(a)}
                    title={t('removeAssignment')}
                    className="w-7 h-7 grid place-items-center rounded hover:bg-rose-500/10 t-muted hover:text-rose-400"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {error && (
        <div className="text-xs text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1.5">
          {error}
        </div>
      )}

      {adding && (
        <AddAssignmentInline
          clientId={clientId}
          assignedRateIds={new Set(items.map((i) => i.rateId))}
          onCancel={() => setAdding(false)}
          onSaved={async () => {
            setAdding(false)
            await reload()
          }}
        />
      )}
    </section>
  )
}

function AddAssignmentInline({
  clientId,
  assignedRateIds,
  onCancel,
  onSaved,
}: {
  clientId: string
  assignedRateIds: Set<string>
  onCancel: () => void
  onSaved: () => void
}) {
  const { t } = useTranslation(['rates', 'common'])
  const [catalog, setCatalog] = useState<RateDto[]>([])
  const [loading, setLoading] = useState(true)
  const [rateId, setRateId] = useState('')
  const [overrideEuros, setOverrideEuros] = useState('')
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<RateListResponse>('/rates?active=true')
      .then((r) => {
        const available = r.items.filter((it) => !assignedRateIds.has(it.id))
        setCatalog(available)
        if (available[0]) setRateId(available[0].id)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [assignedRateIds])

  const selected = catalog.find((c) => c.id === rateId) ?? null

  const onSubmit = async () => {
    if (!rateId) return
    setSubmitting(true)
    setError(null)
    try {
      const overrideNum = overrideEuros.trim()
        ? Number(overrideEuros.replace(',', '.'))
        : null
      if (overrideNum != null && (Number.isNaN(overrideNum) || overrideNum < 0)) {
        setSubmitting(false)
        return setError(t('errors.invalidOverride'))
      }
      await api.post(`/clients/${clientId}/rates`, {
        rateId,
        overrideAmountCents:
          overrideNum != null ? Math.round(overrideNum * 100) : null,
        notes: notes.trim() || null,
      })
      onSaved()
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? t('errors.assignFailed'))
      } else {
        setError(t('errors.network'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="text-xs t-muted flex items-center gap-2 px-2 py-3">
        <Loader2 size={12} className="animate-spin" /> {t('loadingCatalog')}
      </div>
    )
  }

  if (catalog.length === 0) {
    return (
      <div className="text-xs t-faint italic px-2 py-3 flex items-center justify-between">
        {t('noUnassignedRates')}
        <button
          type="button"
          onClick={onCancel}
          className="px-2 py-1 rounded text-xs t-secondary hover:bg-hover"
        >
          {t('common:actions.close')}
        </button>
      </div>
    )
  }

  return (
    <div className="rounded border border-toga-500/30 bg-[var(--bg-input-soft)] p-3 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <select
          value={rateId}
          onChange={(e) => setRateId(e.target.value)}
          className="bg-[var(--bg-input)] border border-[var(--border)] rounded px-2 py-1.5 text-sm t-primary"
        >
          {catalog.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name} — {formatCents(r.amountCents)} {RateKindShort[r.kind]}
            </option>
          ))}
        </select>
        <input
          inputMode="decimal"
          value={overrideEuros}
          onChange={(e) => setOverrideEuros(e.target.value)}
          placeholder={
            selected
              ? t('overridePlaceholderWithBase', { base: (selected.amountCents / 100).toFixed(2) })
              : t('overridePlaceholder')
          }
          className="bg-[var(--bg-input)] border border-[var(--border)] rounded px-2 py-1.5 text-sm t-primary font-mono text-right"
        />
      </div>
      <input
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder={t('notesPlaceholder')}
        className="w-full bg-[var(--bg-input)] border border-[var(--border)] rounded px-2 py-1.5 text-sm t-primary"
      />
      {error && (
        <div className="text-xs text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1">
          {error}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1 rounded text-xs t-secondary hover:bg-hover"
        >
          {t('common:actions.cancel')}
        </button>
        <button
          type="button"
          onClick={() => void onSubmit()}
          disabled={submitting || !rateId}
          className="px-3 py-1 rounded text-xs font-semibold text-white bg-toga-600 hover:bg-toga-500 disabled:opacity-50 flex items-center gap-1"
        >
          {submitting && <Loader2 size={11} className="animate-spin" />}
          {t('assign')}
        </button>
      </div>
    </div>
  )
}
